import React, { useEffect, useRef, useState } from "react";

/*
 * Premium custom cursor — orange accent glow with smooth lag.
 * - Inner dot: tight tracking
 * - Outer ring: soft inertia, expands on interactive hover
 * - Auto-disabled on touch devices
 */
export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const target = useRef({ x: 0, y: 0 });
  const pos = useRef({ x: 0, y: 0 });
  const raf = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    // Disable on touch / coarse pointer devices
    if (typeof window === "undefined") return;
    const hasFine = window.matchMedia("(pointer: fine)").matches;
    setEnabled(hasFine);
    if (!hasFine) return;

    document.body.style.cursor = "none";

    const onMove = (e) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${e.clientX - 4}px, ${e.clientY - 4}px, 0)`;
      }
    };

    const animate = () => {
      pos.current.x += (target.current.x - pos.current.x) * 0.18;
      pos.current.y += (target.current.y - pos.current.y) * 0.18;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${pos.current.x - 18}px, ${pos.current.y - 18}px, 0)`;
      }
      raf.current = requestAnimationFrame(animate);
    };

    const onOver = (e) => {
      const t = e.target;
      if (!t) return;
      const isInteractive = t.closest("a, button, [role='button'], [data-cursor='interactive'], input, textarea, select, label");
      setHover(!!isInteractive);
    };

    const onLeave = () => setHover(false);

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseover", onOver, { passive: true });
    window.addEventListener("mouseout", onLeave, { passive: true });
    raf.current = requestAnimationFrame(animate);

    return () => {
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      window.removeEventListener("mouseout", onLeave);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);

  if (!enabled) return null;

  return (
    <>
      {/* Inner dot — tight follow */}
      <div
        ref={dotRef}
        data-testid="custom-cursor-dot"
        className="pointer-events-none fixed top-0 left-0 z-[9999] w-2 h-2 rounded-full bg-liftile-orange"
        style={{ willChange: "transform" }}
      />
      {/* Outer ring — smooth lag */}
      <div
        ref={ringRef}
        data-testid="custom-cursor-ring"
        className={`pointer-events-none fixed top-0 left-0 z-[9998] rounded-full border transition-[width,height,border-color,background-color,box-shadow] duration-300 ${
          hover
            ? "w-12 h-12 -ml-3 -mt-3 border-liftile-orange/80 bg-liftile-orange/10 shadow-[0_0_20px_rgba(255,107,26,0.45)]"
            : "w-9 h-9 border-liftile-orange/50 shadow-[0_0_12px_rgba(255,107,26,0.3)]"
        }`}
        style={{ willChange: "transform" }}
      />
    </>
  );
}
